import {
	BillingCycle,
	CartItem,
	CartItemAction,
	Currency,
	Env,
	Price,
	addOns,
	dateOrNull,
} from "../../../index";

interface AddOnItemProps {
	internalAddOnId: string;
	internalPlanId: string;
	externalAddOnId: string;
	externalPlanId: string;
	quantity: number;
	recurring: boolean;
	billingCycle: BillingCycle;
	unitPrice: Price;
	totalPrice?: Price;
	createdAt: Date | null;
	updatedAt: Date | null;
}

class AddOnItem {
	constructor(public _: AddOnItemProps) {}

	static fromJson(json: any) {
		const unitPrice = Price.fromJson(json.unit_price);
		return new AddOnItem({
			internalAddOnId: json.internal_addon_id,
			internalPlanId: json.internal_plan_id,
			externalAddOnId: json.external_addon_id,
			externalPlanId: json.external_plan_id,
			quantity: json.quantity,
			recurring: json.recurring,
			billingCycle: BillingCycle.fromJson(json.billing_cycle),
			unitPrice: unitPrice,
			totalPrice: json.total_price
				? Price.fromJson(json.total_price)
				: unitPrice.multiply(json.quantity),
			createdAt: dateOrNull(json.created_at),
			updatedAt: dateOrNull(json.updated_at),
		});
	}

	static fromPaddleData(data: any, env: Env) {
		const unitPrice = Price.fromPaddleItemPrice(data.price);
		const addOn = addOns(env).addOnFromExternalId(data.price.product_id);
		const plan = addOn?.planFromExternalId(data.price.id);
		if (!addOn || !plan) {
			throw new Error("AddOn or Plan not found");
		}
		return new AddOnItem({
			internalAddOnId: addOn._.internalId,
			internalPlanId: plan._.internalId,
			externalAddOnId: addOn._.externalId,
			externalPlanId: plan._.externalId,
			quantity: data.quantity,
			recurring: data.recurring,
			billingCycle: BillingCycle.fromJson(data.price.billing_cycle),
			unitPrice: unitPrice,
			totalPrice: unitPrice.multiply(data.quantity),
			createdAt: dateOrNull(data.created_at),
			updatedAt: dateOrNull(data.updated_at),
		});
	}

	toCartItem() {
		return new CartItem({
			internalAddOnId: this._.internalAddOnId,
			internalPlanId: this._.internalPlanId,
			externalPlanId: this._.externalPlanId,
			action: CartItemAction.ADD,
			price: this._.unitPrice,
			quantity: this._.quantity,
		});
	}

	static fromCartItem(cartItem: CartItem, billingCycle: BillingCycle) {
		return new AddOnItem({
			internalAddOnId: cartItem._.internalAddOnId,
			internalPlanId: cartItem._.internalPlanId,
			externalAddOnId: cartItem._.addOn?._.externalId || "",
			externalPlanId: cartItem._.externalPlanId,
			quantity: cartItem._.quantity,
			recurring: false,
			billingCycle: billingCycle,
			unitPrice:
				cartItem._.price ||
				new Price({ amount: 0, currency: Currency.defaultValue }),
			totalPrice: cartItem._.price?.multiply(cartItem._.quantity),
			createdAt: null,
			updatedAt: null,
		});
	}

	toJson() {
		return {
			internal_addon_id: this._.internalAddOnId,
			internal_plan_id: this._.internalPlanId,
			external_addon_id: this._.externalAddOnId,
			external_plan_id: this._.externalPlanId,
			quantity: this._.quantity,
			recurring: this._.recurring,
			billing_cycle: this._.billingCycle.toJson(),
			unit_price: this._.unitPrice.toJson(),
			total_price: this._.totalPrice?.toJson(),
			created_at: this._.createdAt,
			updated_at: this._.updatedAt,
		};
	}

	copyWith(props: Partial<AddOnItemProps>) {
		return new AddOnItem({ ...this._, ...props });
	}
}

export { AddOnItem };
